import type React from 'react';

/**
 * An avatar shape is stored in kind-0 metadata as the `shape` property.
 * The value is any emoji, used as a CSS mask over the user's avatar.
 * When absent or invalid, avatars render as circles (the default).
 *
 * Reference: https://about.ditto.pub/reference#avatar-shape
 */
export type AvatarShape = string;

/**
 * Checks whether a string could be an emoji shape value.
 * Uses a simple heuristic: short, non-ASCII string.
 */
export function isEmoji(value: string): boolean {
  if (!value || value.length === 0) return false;
  if (value.length > 20) return false;
  // Must contain at least one non-ASCII character
  // eslint-disable-next-line no-control-regex
  return /[^\x00-\x7F]/.test(value);
}

/** Type guard for valid avatar shape values. */
export function isValidAvatarShape(value: unknown): value is AvatarShape {
  if (typeof value !== 'string' || value.length === 0) return false;
  return isEmoji(value);
}

/**
 * Extracts a valid AvatarShape from a parsed kind-0 content object.
 * Returns undefined if the shape is missing or invalid (→ circle).
 */
export function getAvatarShape(content: Record<string, unknown> | undefined): AvatarShape | undefined {
  const raw = content?.shape;
  return isValidAvatarShape(raw) ? raw : undefined;
}

/**
 * CSS filter that creates a crisp outline around a shaped avatar,
 * mimicking `border-4 border-background` without clipping the mask.
 * Apply to a wrapper around the masked element.
 */
export const shapedAvatarBorderStyle: React.CSSProperties = {
  filter:
    'drop-shadow(3px 0 0 rgb(var(--pol-bg)))' +
    ' drop-shadow(-3px 0 0 rgb(var(--pol-bg)))' +
    ' drop-shadow(0 3px 0 rgb(var(--pol-bg)))' +
    ' drop-shadow(0 -3px 0 rgb(var(--pol-bg)))',
};

// ── Emoji mask generation ────────────────────────────────────────────────

/** In-memory cache: emoji → data-URL */
const emojiMaskCache = new Map<string, string>();

/**
 * Renders the emoji onto a canvas and produces a PNG data-URL alpha mask
 * suitable for `mask-image`. Algorithm:
 * 1. Draw large on an oversized canvas
 * 2. Find tight bounding box of non-transparent pixels
 * 3. Square the crop region
 * 4. Redraw onto 256×256 output
 * 5. Convert to alpha mask (white + original alpha)
 */
export function getEmojiMaskUrl(emoji: string): string {
  const cached = emojiMaskCache.get(emoji);
  if (cached) return cached;

  const fontSize = 512;
  const scratch = fontSize * 1.5;
  const c1 = document.createElement('canvas');
  c1.width = scratch;
  c1.height = scratch;
  const ctx1 = c1.getContext('2d');
  if (!ctx1) return '';

  ctx1.textAlign = 'center';
  ctx1.textBaseline = 'middle';
  ctx1.font = `${fontSize}px serif`;
  ctx1.fillText(emoji, scratch / 2, scratch / 2);

  // Find tight bounding box with alpha threshold
  const ALPHA_THRESHOLD = 25;
  const { data: px, width: sw, height: sh } = ctx1.getImageData(0, 0, scratch, scratch);
  let t = sh, b = 0, l = sw, r = 0;
  for (let y = 0; y < sh; y++) {
    for (let x = 0; x < sw; x++) {
      if (px[(y * sw + x) * 4 + 3] > ALPHA_THRESHOLD) {
        if (y < t) t = y;
        if (y > b) b = y;
        if (x < l) l = x;
        if (x > r) r = x;
      }
    }
  }
  if (r < l || b < t) return '';

  // Square the bounding box
  let cropW = r - l + 1;
  let cropH = b - t + 1;
  if (cropW > cropH) {
    const diff = cropW - cropH;
    t -= Math.floor(diff / 2);
    cropH = cropW;
  } else if (cropH > cropW) {
    const diff = cropH - cropW;
    l -= Math.floor(diff / 2);
    cropW = cropH;
  }
  if (t < 0) t = 0;
  if (l < 0) l = 0;

  // Redraw cropped onto output
  const out = 256;
  const c2 = document.createElement('canvas');
  c2.width = out;
  c2.height = out;
  const ctx2 = c2.getContext('2d');
  if (!ctx2) return '';

  ctx2.drawImage(c1, l, t, cropW, cropH, 0, 0, out, out);

  // Convert to alpha mask
  const img = ctx2.getImageData(0, 0, out, out);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    d[i] = 255;
    d[i + 1] = 255;
    d[i + 2] = 255;
  }
  ctx2.putImageData(img, 0, 0);

  const url = c2.toDataURL('image/png');
  emojiMaskCache.set(emoji, url);
  return url;
}

/**
 * Get mask URL for an avatar shape.
 * Returns empty string if invalid.
 */
export function getAvatarMaskUrl(shape: string): string {
  if (isEmoji(shape)) {
    return getEmojiMaskUrl(shape);
  }
  return '';
}
